// =====================================================================
// en.js — English translations + content data.
// All translatable strings/arrays for every section.
// Numbers, prices, images, hrefs are in static.js.
// =====================================================================

export const en = {
  code: "en",
  label: "EN",
  switchTo: "हिं", // label of the OTHER language (what user can switch to)
  switchAria: "Switch to Hindi",

  site: {
    tagline: "Royal journeys, crafted by locals.",
    description:
      "Premium cabs, curated tour packages and seamless one-way taxis across the Pink City and beyond.",
    address: "Jaipur, Rajasthan, India",
  },

  nav: {
    home: "Home",
    cabs: "Cab Services",
    oneway: "One-Way",
    packages: "Packages",
    destinations: "Stories",
    customTrip: "Custom Trip",
    bookOnWhatsApp: "Book on WhatsApp",
    brandLine1: "Jaipur Tours",
    brandLine2: "& Travels",
  },

  hero: {
    badge: "Based in Jaipur · Serving all India",
    h1Part1: "Reliable Cabs &",
    h1Italic: "Sightseeing Tours",
    h1Part2: "in Jaipur.",
    subhead:
      "Whether you need a local taxi, an outstation drop to Delhi or Agra, or a complete Rajasthan holiday package, we make it easy. Honest pricing, verified drivers, and instant booking via WhatsApp.",
    ctaPrimary: "Book on WhatsApp",
    ctaSecondary: "Explore Packages",
    ratingValue: "4.7",
    ratingTrips: "2,400+ trips",
    verified: "Verified drivers · GPS-tracked rides",
    heroChip: "Hawa Mahal · Jaipur",
    todaysPick: "Today's pick",
    pickTitle: "1-Day Jaipur Sightseeing",
    pickPriceLabel: "From",
    pickPriceSuffix: "· Sedan",
    seeItinerary: "See itinerary",
    waMsg: "Hi Jaipur Tours & Travels, I'd like to book a cab/package. Please share details.",
  },

  cabs: {
    eyebrow: "Fleet · KM Basis",
    title: "Pick your ride.",
    titleItalic: "Pay only for distance.",
    subtitle:
      "Outstation & local cabs from compact sedans to luxury Urbania coaches. Transparent km-basis pricing — no hidden surcharges.",
    seater: "Seater",
    perKm: "per km",
    enquireNow: "Enquire Now",
    termsBadge: "Heads up",
    termsTitle: "Fare Terms & Conditions",
    termsSubtitle: "Applies to all km-basis bookings above.",
    waMsg: (name, sub) =>
      `Hi Jaipur Tours & Travels, I want to book a ${name} (${sub}) on km basis. Please share availability.`,
    fleet: [
      {
        id: "sedan", name: "Sedan", subtitle: "Dzire / Aura",
        features: ["AC", "Music System", "2 Luggage", "Pet Friendly"]
      },
      {
        id: "ertiga", name: "Ertiga", subtitle: "Maruti Suzuki Ertiga",
        features: ["AC", "Spacious", "3 Luggage", "Family Friendly"]
      },
      {
        id: "innova", name: "Innova", subtitle: "Toyota Innova",
        features: ["AC", "Premium Ride", "4 Luggage", "Long Tours"]
      },
      {
        id: "innova-crysta", name: "Innova Crysta", subtitle: "Toyota Crysta",
        features: ["Captain Seats", "Premium AC", "5 Luggage", "Plush Interior"]
      },
      {
        id: "tempo-traveller", name: "Tempo Traveller", subtitle: "Force Traveller · 12 & 16 seater",
        features: ["AC", "Recliner Seats", "Big Boot", "Group Travel"]
      },
      {
        id: "urbania", name: "Urbania", subtitle: "Force Urbania · 12 & 16 seater",
        features: ["Luxury Coach", "Push-Back Seats", "Charging Ports", "Premium"]
      },
    ],
    terms: [
      "Minimum running of 250 km per day.",
      "Night charge of ₹300 ($4) applies after 11 PM.",
      "Parking, Toll Tax and State Tax are not included.",
      "Driver allowance applicable for multi-day trips.",
    ],
  },

  oneway: {
    eyebrow: "One-Way Drops",
    title: "Pay one-side.",
    titleItalic: "Save big.",
    subtitle:
      "No empty-return charges. Confirmed pickup with verified drivers.",
    note: "Starting rates · pricing varies by vehicle & dates",
    bookNow: "Book Now",
    startsFrom: "Starts from",
    km: "km",
    waMsg: (from, to) =>
      `Hi Jaipur Tours & Travels, I want a one-way taxi from ${from} to ${to}. Please share availability and final price.`,
    routes: [
      {
        id: "jpr-del", from: "Jaipur", to: "Delhi",
        highlights: ["Express Highway", "Pickup at Doorstep", "24x7 Available"]
      },
      {
        id: "jpr-agr", from: "Jaipur", to: "Agra",
        highlights: ["Visit Taj Mahal", "Fatehpur Sikri Detour", "Flexible Pickup"]
      },
      {
        id: "jpr-ajm", from: "Jaipur", to: "Ajmer",
        highlights: ["Ajmer Sharif Dargah", "Pushkar Add-on", "Fast Highway"]
      },
    ],
  },

  packages: {
    eyebrow: "Curated Packages",
    title: "Hand-stitched tours.",
    titleItalic: "Built by local insiders.",
    subtitle:
      "Every package below is a real, road-tested itinerary — not a generic template. Swap your vehicle, pick your pace.",
    itineraryHeader: "Itinerary",
    foodStops: "Food stops:",
    includes: "Includes",
    totalPackage: "Total Cost",
    bookNow: "Book Now",
    waMsg: (title, vehicle) =>
      `Hi Jaipur Tours & Travels, I want to book the "${title}" in a ${vehicle}. Please share details and confirm the final price.`,
    list: [
      {
        id: "jpr-1day",
        title: "1-Day Jaipur Sightseeing",
        duration: "1 Day · 8–10 hrs",
        tag: "Most Booked",
        summary:
          "A perfectly paced loop through the Pink City's iconic forts, palaces and food legends — done right.",
        itinerary: [
          { time: "08:30", place: "Amber Fort", note: "Mirror palace & ramparts" },
          { time: "10:30", place: "Elephant Village", note: "Hathi Gaon visit" },
          { time: "11:30", place: "Jal Mahal", note: "Lake palace photo stop" },
          { time: "12:30", place: "Rawat Mishthan Bhandar", note: "Pyaaz kachori break" },
          { time: "14:00", place: "City Palace", note: "Royal courtyards" },
          { time: "15:30", place: "Hawa Mahal", note: "Pink lattice façade" },
          { time: "17:00", place: "LMB, Johari Bazaar", note: "Thali lunch / snacks" },
          { time: "18:30", place: "Nahargarh Fort", note: "Sunset over Jaipur" },
        ],
        foodStops: ["Rawat Mishthan Bhandar", "Lakshmi Mishthan Bhandar (LMB)"],
        inclusions: [
          "Pickup & drop from your hotel",
          "Professional driver-guide",
          "Fuel + state permit",
          "Bottled water",
        ],
        notes:
          "Neat and clean cabs with highly professional and experienced drivers.",
        priceLabels: ["", "", "", "", ""],
      },
      {
        id: "jpr-2day",
        title: "2-Day Royal Jaipur Experience",
        duration: "2 Days · 1 Night",
        tag: "Heritage Pick",
        summary:
          "A deeper royal immersion — forts by day, Chokhi Dhani by night. Curated for first-timers who want it all.",
        itineraryByDay: [
          {
            day: "Day 1",
            label: "Forts, lakes & city lights",
            stops: [
              { place: "Amber Fort + Jaigarh", note: "Royal fortress trail" },
              { place: "Nahargarh Fort", note: "Hilltop panorama" },
              { place: "Elephant Village & Jal Mahal", note: "Cultural stop" },
              { place: "World Trade Park (WTP)", note: "Modern Jaipur · shopping & dinner" },
              { place: "Moj Mahal", note: "Evening leisure" },
            ],
          },
          {
            day: "Day 2",
            label: "Palaces, temples & a folk night",
            stops: [
              { place: "City Palace + Jantar Mantar", note: "UNESCO marvels" },
              { place: "Hawa Mahal", note: "Pink architecture" },
              { place: "Birla Mandir", note: "Marble temple at Moti Doongri" },
              { place: "Gaitore ki Chhatriyan", note: "Royal cenotaphs" },
              { place: "Chokhi Dhani", note: "Folk village dinner" },
            ],
          },
        ],
        inclusions: [
          "Neat & clean AC cab",
          "Professional, experienced driver",
          "Door-to-door pickup",
          "Custom timing on request",
        ],
        accommodation:
          "Hotels can be chosen by the guest, or we can optionally arrange accommodations upon request.",
        notes:
          "Neat and clean cabs with highly professional and experienced drivers.",
        priceLabels: ["", "", "", "", ""],
      },
      {
        id: "pilgrim",
        title: "Religious Pilgrim Package",
        duration: "1–2 Days · Flexible",
        tag: "Spiritual",
        summary:
          "A devotional circuit to Shekhawati's most-loved shrines — calm, well-paced and family friendly.",
        itinerary: [
          { time: "Stop 1", place: "Khatu Shyam Ji", note: "Darshan at the famed temple" },
          { time: "Stop 2", place: "Salasar Balaji", note: "Hanuman ji blessings" },
          { time: "Stop 3", place: "Rani Sati Dadi, Jhunjhunu", note: "Historic shrine visit" },
        ],
        inclusions: [
          "Hotel/home pickup",
          "Experienced driver familiar with route",
          "Flexible darshan timings",
          "Multi-stop allowed",
        ],
        notes:
          "Pricing calculated on standard km basis per fleet selection (approx. 550 km round trip).",
        priceLabels: ["", "", "", ""],
        heroImageLabels: {
          "khatu-shyam": "Khatu Shyam Ji",
          "salasar": "Salasar Balaji",
          "rani-sati": "Rani Sati Dadi",
        },
      },
    ],
  },

  testimonials: {
    eyebrow: "Travellers' words",
    title: "Stories from the",
    titleItalic: "Pink City road.",
    list: [
      {
        id: "t1", name: "Priya Sharma", location: "Bengaluru", trip: "1-Day Jaipur Sightseeing · Innova", rating: 5,
        quote: "Our driver Manoj knew every shortcut and the best timing for Amber Fort photos. The pyaaz kachori stop at Rawat was a delicious surprise. Felt like a local-led tour, not a tourist trap."
      },
      {
        id: "t2", name: "Rohit Mehta", location: "Mumbai", trip: "2-Day Royal Jaipur · Innova Crysta", rating: 5,
        quote: "Booked the 2-day package for our anniversary. Chokhi Dhani in the evening was magical. Cab was spotless, driver was a complete gentleman with our family. Worth every rupee."
      },
      {
        id: "t3", name: "Anjali & Family", location: "Delhi", trip: "Pilgrim Package · Ertiga", rating: 5,
        quote: "Khatu Shyam Ji to Salasar Balaji in one trip with my elderly parents — flexible timings, comfortable Ertiga, no rush at all. Driver waited patiently at every darshan. Highly recommended."
      },
      {
        id: "t4", name: "Karan Singh", location: "Gurugram", trip: "Jaipur → Delhi One-Way · Sedan", rating: 5,
        quote: "Last-minute one-way booking on WhatsApp at 10 PM — confirmed in 4 minutes. Driver reached my hotel at 6 AM sharp. Clean Dzire, smooth highway run. This is how taxi bookings should feel."
      },
      {
        id: "t5", name: "Meera Iyer", location: "Pune", trip: "Custom 5-day Rajasthan · Tempo Traveller", rating: 5,
        quote: "We were 11 people, 5 cities, lots of luggage. Anil bhaiya from JT&T crafted the route, handled hotel pickups, and the Tempo Traveller was actually comfortable. Best travel decision we made."
      },
      {
        id: "t6", name: "Vikram Rao", location: "Hyderabad", trip: "Jaipur → Agra One-Way · Innova", rating: 5,
        quote: "Took the Jaipur-Agra route with a Fatehpur Sikri detour. Driver knew the back roads and saved us 45 minutes. Honest pricing, no surprises on toll/tax. Will book again."
      },
    ],
    prev: "Previous testimonial",
    next: "Next testimonial",
  },

  custom: {
    eyebrow: "Custom Itinerary",
    title: "Don't see your trip?",
    titleItalic: "We'll design it.",
    body:
      "Multi-city Rajasthan, family weddings, photography expeditions, pilgrim circuits — tell us the dream, we'll quote the route, the car and the stay. One WhatsApp message away.",
    cta1: "Start on WhatsApp",
    cta2: "Call",
    waMsg: "Hi Jaipur Tours & Travels, I'd like to plan a custom Rajasthan trip. Here are my initial thoughts: ",
    features: [
      {
        title: "Routes built around you",
        desc: "Half-day, multi-city or 10-day circuits — we sketch them around your dates, energy and budget."
      },
      {
        title: "Flexible scheduling",
        desc: "Want sunrise at Amber? Late-night Chokhi Dhani return? Tell us — drivers stay on your clock."
      },
      {
        title: "Local human support",
        desc: "A real Jaipur-based human on WhatsApp — not a chatbot. Replies usually within minutes."
      },
    ],
  },

  footer: {
    explore: "Explore",
    contact: "Get in touch",
    crafted: "Crafted with care in Jaipur",
    rights: "All rights reserved.",
    message: "Message on WhatsApp",
    waMsg: "Hi Jaipur Tours & Travels, I'd like to enquire.",
  },

  widget: {
    eyebrow: "Chat with us",
    waMsg: "Hi Jaipur Tours & Travels, I'd like to enquire about your cabs/packages.",
  },

  trustStats: [
    { value: "10+", label: "Years on Jaipur roads" },
    { value: "20k+", label: "Happy travellers" },
    { value: "4.7★", label: "Average rating" },
    { value: "24/7", label: "Support, every day" },
  ],

  blog: {
    eyebrow: "Stories & Guides",
    title: "Destinations & Travel Guides",
    subtitle:
      "Stories, tips and road-tested routes from Rajasthan — to help you plan smarter.",
    readMore: "Read more",
    readTime: "min read",
    publishedOn: "Published",
    back: "← Back to all stories",
    related: "More stories",
    notFound: "Story not found.",
    notFoundCta: "Back to all stories",
    waMsg: (title) =>
      `Hi Jaipur Tours & Travels, I read your guide "${title}". I'd like to plan a trip.`,
    bookCta: "Plan this trip on WhatsApp",
    articles: [
      {
        slug: "best-time-to-visit-jaipur",
        title: "Best Time to Visit Jaipur: A Month-by-Month Guide",
        excerpt:
          "October to March is the sweet spot — but every season in the Pink City has its own colour. Here's an honest, local guide to picking your dates.",
        author: "Jaipur Tours & Travels",
        body: [
          { type: "p", text: "Jaipur wears a different face every season. From the misty forts of January to the sun-bleached afternoons of June, the city always has something to offer — you just need to pick your moment well." },
          { type: "h2", text: "The Winter Window (October – March)" },
          { type: "p", text: "If we had to pick one window, this would be it. Days are crisp (18–28°C), the light is golden, and the famous Hawa Mahal photographs are at their cleanest. Diwali through Republic Day is peak season — book your cab and hotel at least 2 weeks in advance." },
          { type: "p", text: "Tip: aim for a weekday in early November. You'll get the same weather without the queues at Amber Fort." },
          { type: "h2", text: "Shoulder Months (September & April)" },
          { type: "p", text: "Often underrated. September brings the tail of the monsoon — Aravalli hills look spectacular green, and crowds thin out. April is warm (32–38°C) but mornings and evenings are pleasant. Best value for money for our 2-Day Royal Jaipur Experience." },
          { type: "h2", text: "Summer (May – June)" },
          { type: "p", text: "Hot — we won't sugarcoat it. Afternoons hit 42°C+. But it's a great time for indoor heritage (City Palace museums, Albert Hall) and very competitive cab pricing. Start sightseeing at 6 AM, rest by noon." },
          { type: "h2", text: "Monsoon (July – August)" },
          { type: "p", text: "Most romantic. Nahargarh at sunset, mist rolling off the Aravallis, peacocks calling — magic. Roads can get slippery on the Amber climb, so a Sedan or Innova with an experienced local driver is ideal." },
          { type: "h2", text: "Quick Pick by Trip Type" },
          {
            type: "ul", items: [
              "First-time tourist: November or February",
              "Photographers: late August or December mornings",
              "Pilgrim circuit (Khatu Shyam): October or March",
              "Budget travellers: September or April",
            ]
          },
        ],
      },
      {
        slug: "khatu-shyam-darshan-guide",
        title: "Khatu Shyam Ji Darshan: A First-Timer's Complete Guide",
        excerpt:
          "Timings, dress code, the famous Falgun Mela, and why most pilgrims combine it with Salasar Balaji in one trip. Everything you need before you go.",
        author: "Jaipur Tours & Travels",
        body: [
          { type: "p", text: "Khatu Shyam Ji is one of Rajasthan's most visited shrines — and one of the most heartfelt. If you're planning your first darshan, here's a practical guide based on hundreds of trips we've driven." },
          { type: "h2", text: "Where & How Far" },
          { type: "p", text: "Khatu is in Sikar district, about 80 km north of Jaipur via NH-52. Drive time is roughly 1.5 hours by Sedan in normal traffic. Most pilgrims couple it with Salasar Balaji (another 90 km north-west) and Rani Sati Dadi at Jhunjhunu for a full Shekhawati circuit." },
          { type: "h2", text: "Best Time of Day" },
          { type: "p", text: "Morning darshan (4:30 AM – 12:30 PM) is calmest. Avoid Sundays unless you have time to spare — queues can stretch 2 hours. Ekadashi and Falgun Mela days draw lakhs of devotees; plan accordingly or come a week before/after." },
          { type: "h2", text: "Dress Code & Etiquette" },
          { type: "p", text: "Modest, comfortable cotton works best. Cover shoulders and knees. Mobile phones, leather items and outside flowers are not allowed inside the sanctum — leave them in the locker (free) or with your driver." },
          { type: "h2", text: "Falgun Mela (February – March)" },
          { type: "p", text: "The annual fair is spectacular but crowded — over 10 lakh visitors in 10 days. Book accommodation in Reengus or Sikar 4–6 weeks ahead. If you're driving, road closures from 5 AM are common, so an experienced local driver is invaluable." },
          { type: "h2", text: "How to Combine With Salasar Balaji" },
          { type: "p", text: "Our Pilgrim Package covers Khatu Shyam → Salasar Balaji → Rani Sati Dadi in roughly 550 km round-trip from Jaipur. Comfortably done in one long day (12+ hours) or split across two days with an overnight stay at Salasar." },
          { type: "h2", text: "Pro Tips" },
          {
            type: "ul", items: [
              "Carry small change for offerings and parking",
              "Eat at Khatu's local 'jalebi' shops — a sweet ritual after darshan",
              "Photography is allowed in the outer courtyards only",
              "Book a return cab in advance — peak-time auto-rickshaws spike 3x",
            ]
          },
        ],
      },
      {
        slug: "jaipur-to-agra-road-trip",
        title: "Jaipur to Agra Road Trip: Routes, Stops & Honest Timings",
        excerpt:
          "240 km, two expressways, and a hidden Mughal town in between. Everything you need to make the Pink-City-to-Taj run feel like a holiday, not a transfer.",
        author: "Jaipur Tours & Travels",
        body: [
          { type: "p", text: "The Jaipur–Agra drive is one of India's most-driven heritage routes. Two World Heritage cities, 240 km, and — if planned right — a road trip worth remembering on its own." },
          { type: "h2", text: "Two Routes, One Decision" },
          { type: "p", text: "Route A (faster): NH-21 + Yamuna Expressway via Bharatpur — 4h 30m, smoother surface, fewer scenic stops. Route B (heritage): via Fatehpur Sikri — adds 45 minutes but gifts you the UNESCO ghost city. We recommend B if you have time." },
          { type: "h2", text: "Best Stops on the Way" },
          { type: "p", text: "Bharatpur Bird Sanctuary (Keoladeo National Park) — perfect 90-minute detour for nature lovers, especially November–February. Fatehpur Sikri's Buland Darwaza is breathtaking at golden hour. Light dhaba lunch at Mehtab Bagh side has the best parathas on the route." },
          { type: "h2", text: "When to Leave Jaipur" },
          { type: "p", text: "Leave by 6:30 AM. You'll reach Fatehpur Sikri by 10:30, finish by noon, and arrive in Agra in time for an afternoon Taj visit (Taj closes Fridays — plan for any other weekday)." },
          { type: "h2", text: "Vehicle Recommendation" },
          { type: "p", text: "For 2 adults + light luggage, a Sedan (Dzire) at ₹11/km ($0.13/km) is most economical. For 4 adults with bags or seniors, an Innova at ₹16/km ($0.19/km) is the sweet spot — captain seats make the highway smoother. Avoid Tempo Traveller for fewer-than-8 passengers; the Innova rides better on this route." },
          { type: "h2", text: "Toll & Charges to Expect" },
          {
            type: "ul", items: [
              "Tolls (round trip): ~₹950 ($12)",
              "Fatehpur Sikri parking + entry: ~₹100 ($1.50)",
              "Driver allowance (if staying overnight): ₹400 ($5)",
              "State permit (if applicable): ~₹200 ($2.50)",
            ]
          },
          { type: "h2", text: "One-Way vs Round Trip" },
          { type: "p", text: "If you only need to go to Agra, our one-way taxi (₹3,000 / $36 onwards, Sedan) saves you the empty-return charge. If you want to come back the same day, a round trip with a 90-minute Taj viewing window is the most popular option — we've driven hundreds." },
        ],
      },
    ],
  },
};
